import React, { useState } from "react";
import Select, {
  components,
  type ActionMeta,
  type MultiValue,
  type OptionProps,
  type StylesConfig,
} from "react-select";

/**
 * Drop-in replacement for the abandoned `react-multiselect-checkboxes` package,
 * built on react-select@5 so it runs on React 19. It reproduces the original
 * API surface (dropdown button + checkbox option menu) so existing call sites
 * only need their import path changed.
 */

export interface MultiSelectOption {
  label: string;
  value: string | number;
}

type StyleFn = (provided: Record<string, unknown>, state?: unknown) => Record<string, unknown>;

interface DropdownButtonLabelArgs {
  placeholderButtonLabel?: string;
  value: readonly MultiSelectOption[];
}

interface MultiSelectCheckboxesProps {
  options: readonly unknown[];
  value?: readonly MultiSelectOption[];
  onChange?: (
    value: MultiValue<MultiSelectOption>,
    actionMeta: ActionMeta<MultiSelectOption>
  ) => void;
  styles?: { [key: string]: StyleFn };
  placeholderButtonLabel?: string;
  getDropdownButtonLabel?: (args: DropdownButtonLabelArgs) => string;
  hideSearch?: boolean;
  isDisabled?: boolean;
  className?: string;
  classNamePrefix?: string;
}

// Mirrors react-multiselect-checkboxes' defaultDropdownButtonStyle so callers
// that spread `...provided` in their dropdownButton style keep the same look.
const defaultDropdownButtonStyle: React.CSSProperties = {
  padding: "8px 8px 5px 8px",
  backgroundColor: "#FFF",
  borderRadius: "2px",
  borderWidth: 0,
  boxShadow: "0 1px 1px 1px rgba(0, 0, 0, 0.08)",
  fontStyle: "normal",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: 1.36,
  alignItems: "baseline",
  background: "#FFF",
  boxSizing: "border-box",
  borderColor: "hsl(0, 0%, 80%)",
  color: "#515151",
  cursor: "pointer",
  display: "inline-flex",
  margin: 0,
  maxWidth: "100%",
  flexWrap: "nowrap",
  textAlign: "left",
  textDecoration: "none",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
  width: "auto",
};

const iconSpan: React.CSSProperties = {
  alignSelf: "center",
  display: "flex",
  flexShrink: 0,
  lineHeight: 0,
  margin: "0px 4px",
};

const textSpan: React.CSSProperties = {
  alignItems: "center",
  alignSelf: "center",
  flex: "1 1 auto",
  margin: 0,
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const menuStyle: React.CSSProperties = {
  backgroundColor: "white",
  borderRadius: 4,
  boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1), 0 4px 11px rgba(0, 0, 0, 0.1)",
  marginTop: 8,
  position: "absolute",
  zIndex: 2,
};

const blanketStyle: React.CSSProperties = {
  bottom: 0,
  left: 0,
  top: 0,
  right: 0,
  position: "fixed",
  zIndex: 1,
};

const defaultGetDropdownButtonLabel = ({
  placeholderButtonLabel,
  value,
}: DropdownButtonLabelArgs): string => {
  if (!value || value.length === 0) return placeholderButtonLabel ?? "Select...";
  if (value.length === 1) return value[0].label;
  return `${value.length} selected`;
};

const CheckboxOption = (props: OptionProps<MultiSelectOption, true>) => (
  <components.Option {...props}>
    <input
      type="checkbox"
      readOnly
      checked={props.isSelected}
      style={{ marginRight: 8 }}
    />
    {props.label}
  </components.Option>
);

const MultiSelectCheckboxes: React.FC<MultiSelectCheckboxesProps> = ({
  options,
  value,
  onChange,
  styles = {},
  placeholderButtonLabel = "Select...",
  getDropdownButtonLabel = defaultGetDropdownButtonLabel,
  hideSearch = false,
  isDisabled = false,
  className,
  classNamePrefix,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = () => {
    if (!isDisabled) setIsOpen((open) => !open);
  };

  const { dropdownButton, ...selectStyles } = styles;
  const currentValue = value ?? [];

  const buttonStyle = dropdownButton
    ? (dropdownButton(
        defaultDropdownButtonStyle as Record<string, unknown>,
        { value: currentValue, isOpen }
      ) as React.CSSProperties)
    : defaultDropdownButtonStyle;

  const selectComponents: Record<string, unknown> = { Option: CheckboxOption };
  if (hideSearch) {
    selectComponents.Control = () => null;
  }

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        style={buttonStyle}
        onClick={toggleOpen}
        disabled={isDisabled}
      >
        <span style={textSpan}>
          {getDropdownButtonLabel({ placeholderButtonLabel, value: currentValue })}
        </span>
        <span style={iconSpan} aria-hidden="true">
          <svg width="10" height="7" viewBox="0 0 10 7" fill="none">
            <path
              d="M1 1l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {isOpen && (
        <>
          <div style={menuStyle}>
            <Select<MultiSelectOption, true>
              autoFocus
              isMulti
              closeMenuOnSelect={false}
              backspaceRemovesValue={false}
              controlShouldRenderValue={false}
              hideSelectedOptions={false}
              isClearable={false}
              menuIsOpen
              tabSelectsValue={false}
              components={selectComponents}
              styles={selectStyles as unknown as StylesConfig<MultiSelectOption, true>}
              value={value}
              options={options as never}
              onChange={(newValue, actionMeta) => onChange?.(newValue, actionMeta)}
              className={className}
              classNamePrefix={classNamePrefix}
              placeholder="Search..."
            />
          </div>
          <div style={blanketStyle} onClick={() => setIsOpen(false)} />
        </>
      )}
    </div>
  );
};

export default MultiSelectCheckboxes;
